import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import styles from "./styles.module.scss";
import { isNull } from "lodash";
import OfferingOption from "../OfferingOption";
import PaymentGuardModal, {
  PaymentGuardChoice,
  PaymentGuardObjectType,
} from "../PaymentGuardModal";
import SaveBar from "app/routes/Sessions/SaveBar";
import usePaymentGuard from "app/utils/hooks/usePaymentGuard";
import ClinicSvg from "../../../../../public/images/clinic-icon.svg";
import Dollar from "../../../../../public/images/dollar-circle-icon.svg";
import { useTranslation } from "react-i18next";
interface Option {
  title: string;
  label: string;
  subtitle: string;
  image: string;
}

export default function OfferingOptions() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Option | null>(null);
  const [guardOpen, setGuardOpen] = useState(false);
  const history = useHistory();

  // Check for facility context from URL
  const urlParams = new URLSearchParams(window.location.search);
  const facilityId = urlParams.get('facilityId');
  const queryString = facilityId ? `?facilityId=${facilityId}` : '';

  // Academy coaches pick how a new package or subscription is paid before
  // the form opens; see usePaymentGuard for the rule.
  const guard = usePaymentGuard(facilityId);
  const guardedType: PaymentGuardObjectType =
    selected?.label === "Payment" ? "package" : "subscription";

  const openOfferingForm = (collectType?: PaymentGuardChoice) => {
    const path =
      guardedType === "package" ? "/payment/create" : "/subscription/create";
    history.push(
      `${path}${queryString}`,
      collectType ? { collectType } : undefined,
    );
  };

  const onSubmit = () => {
    if (guard.required) {
      setGuardOpen(true);
      return;
    }
    // Without the guard the form decides from the fetched Stripe status
    // (offline only while unconnected) and reports the field itself.
    openOfferingForm();
  };
  const handleSelection = (item) => {
    setSelected(item);
  };
  const offeringOptions: Option[] = [
    {
      title: t("PACKAGE"),
      label: "Payment",
      subtitle:
        t("Create a single-payment package. Include automatically tracked booking options, manual session tracking, or sell products that don't require schedule tracking."),
       image: ClinicSvg,
    },
    {
      title: t("SUBSCRIPTION"),
      label: "Subscription",
      subtitle:
        t("Create monthly or annual subscription offerings."),
       image: Dollar,
    },
  ];

  return (
    <div>
      <div className={`${styles.empty_container} billing-page-container`}>
        <div>
          <div className={styles.container}>
            <p className={styles.bold_text}>
              {t('Add New Package or Subscription')}
            </p>
            <div
              className={`${styles.program_options_line_container} ${styles.mt_20}`}
            >
              {offeringOptions.map((item, index) => {
                return (
                  <OfferingOption
                    key={index}
                    title={item.title}
                    subtitle={item.subtitle}
                    DisplayImage={item.image}
                    selected={selected?.title === item.title}
                    onClick={() => handleSelection(item)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <SaveBar
        onSubmit={onSubmit}
        showCancel={true}
        onCancel={() => {
          history.push(facilityId ? "/facility_billing" : "/billing");
        }}
        // The guard's Online option follows a status that is only known once
        // the scheduling profile (or facility) has loaded; until then the
        // choice — which can never be changed afterwards — must not be offered.
        submitDisabled={
          isNull(selected) || (guard.academyCoach && !guard.stripeStatusKnown)
        }
      />
      <PaymentGuardModal
        isOpen={guardOpen}
        objectType={guardedType}
        stripeConnected={guard.stripeConnected}
        stripeStatusKnown={guard.stripeStatusKnown}
        // Cancel, the close icon, Escape and the overlay only close the guard:
        // nothing is created or marked. The form opens only through Confirm
        // with a selected option (product decision 2026-09-16).
        onClose={() => setGuardOpen(false)}
        onConfirm={(choice) => {
          setGuardOpen(false);
          guard.confirm(choice);
          openOfferingForm(choice);
        }}
      />
    </div>
  );
}
